import { jsx, jsxs } from "react/jsx-runtime";
import { Box, Stack, Heading, Columns, Column, Text, IconTick, IconClear } from "braid-design-system";
import { getProductIsSelectedLoading, getGridColumn, HelpTip } from "../../GridLayout.mjs";
import { Grid } from "../../../shared/components/Grid/Grid.mjs";
import { GridColumn } from "../../../shared/components/Grid/GridColumn/GridColumn.mjs";
import { getPanelStyle } from "../../../shared/utils/getPanelStyle/getPanelStyle.mjs";
import { collapsedMatrix, panelReset, panelValuesFeatured, panelValuesFeaturedSelected, panelValues, panelValuesSelected, panel } from "./ComparativesMatrix.css.mjs";
import { gridContainer } from "../../GridLayout.css.mjs";
import { columnPanel } from "../../../shared/SharedLayout.css.mjs";
const COMPARATIVE_ATTRIBUTES_PERFORMANCE = [
  {
    key: "applications",
    title: "Applications",
    description: "Number of applications you are estimated to receive"
  },
  {
    key: "views",
    title: "Views",
    description: "Number of times your job ad is estimated to be viewed"
  }
];
const COMPARATIVE_ATTRIBUTES_FEATURES = [
  {
    key: "listing-duration",
    title: "Listing duration",
    description: "The length of time your job ad will remain active"
  },
  {
    key: "visibility-search",
    title: "Visibility in search",
    description: "Level of visibility your ad receives when candidates search on SEEK",
    helpTip: {
      content: [
        "Level of visibility your ad receives when candidates search on SEEK.",
        "Get noticed with greater exposure across search results."
      ]
    }
  },
  {
    key: "visibility-recommendations",
    title: "Visibility in recommendations",
    description: "Level of visibility your ad receives in our recommendations channels",
    helpTip: {
      content: [
        "Level of visibility your ad receives in our recommendations channels.",
        "Get noticed with greater exposure across our recommendation channels."
      ]
    }
  },
  {
    key: "candidate-invitations",
    title: "Candidate invitations",
    description: "Personally invite top candidates to apply for your role",
    helpTip: {
      content: [
        "Personally invite top candidates to apply for your role.",
        "Receive 1000 credits to use on our platform to personally invite candidates to apply for your role."
      ]
    }
  },
  {
    key: "access-to-analytics",
    title: "Access to analytics",
    description: "Access to analytics and reporting about your ad"
  },
  {
    key: "branding",
    title: "Branding",
    description: "Your company’s logo and key selling points on the ad",
    helpTip: {
      content: [
        "Your company’s logo and key selling points on the ad.",
        "Promote your brand to attract the right candidate."
      ],
      image: {
        id: "branding-logo-and-cover",
        description: "Logo and cover image shown on an ad"
      }
    }
  },
  {
    key: "ad-writing-service",
    title: "Ad writing service",
    description: "Professional assistance in crafting a compelling job ad"
  },
  {
    key: "money-back",
    title: "Moneyback guarantee",
    description: "Money back if you do not place with SEEK"
  }
];
const GRID_TEMPLATE_AREAS = COMPARATIVE_ATTRIBUTES_PERFORMANCE.concat(
  COMPARATIVE_ATTRIBUTES_FEATURES
).map(({ key }) => `"${key}"`).join(" ");
const getIsLastCell = ({
  attributes,
  attributeIndex
}) => attributeIndex === attributes.length - 1;
const filterAttributes = (products) => (attribute) => products.find(
  (product) => {
    var _a, _b;
    return Boolean(
      (_b = (_a = product == null ? void 0 : product.comparatives) == null ? void 0 : _a.content) == null ? void 0 : _b.find(
        (comparative) => comparative.key === attribute.key
      )
    );
  }
);
const SECTIONS = [
  {
    title: "Estimated ad performance",
    attributes: COMPARATIVE_ATTRIBUTES_PERFORMANCE
  },
  { title: "Features", attributes: COMPARATIVE_ATTRIBUTES_FEATURES }
];
const ComparativesMatrix = ({
  alignValue,
  numOfColumns,
  gridRowGap,
  products,
  selectedProductIds,
  selectedParentProduct,
  variant = "default",
  isExpanded = true
}) => /* @__PURE__ */ jsx(Box, { className: [!isExpanded ? collapsedMatrix : ""], children: /* @__PURE__ */ jsx(Stack, { space: "medium", children: SECTIONS.map(({ title, attributes }) => /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
  /* @__PURE__ */ jsx(ComparativesMatrixSectionTitle, { title }),
  /* @__PURE__ */ jsx(
    ComparativesMatrixSection,
    {
      ...{
        alignValue,
        numOfColumns,
        gridRowGap,
        attributes: attributes.filter(filterAttributes(products)),
        variant,
        products,
        selectedProductIds,
        selectedParentProduct,
        isExpanded
      }
    }
  )
] }, title)) }) });
const ComparativesMatrixSection = ({
  alignValue,
  numOfColumns,
  gridRowGap,
  attributes,
  variant,
  products,
  selectedProductIds,
  selectedParentProduct
}) => /* @__PURE__ */ jsxs(Grid, { align: alignValue, className: [gridContainer], children: [
  /* @__PURE__ */ jsx(
    ComparativesMatrixColumn,
    {
      numOfColumns,
      gridRowGap,
      customStyles: [panelReset],
      children: attributes.map((attribute, attributeIndex) => /* @__PURE__ */ jsx(
        ComparativeCellWrapper,
        {
          isLastCell: getIsLastCell({
            attributes,
            attributeIndex
          }),
          children: /* @__PURE__ */ jsx(ComparativeHeadingCell, { attribute, variant })
        },
        attribute.key
      ))
    }
  ),
  products.map((product, index) => {
    var _a;
    const isCurrentProduct = product.purchaseState === "PURCHASED";
    const { isSelected } = getProductIsSelectedLoading({
      selectedProductIds,
      loadingProductIds: [],
      product,
      forceSelected: isCurrentProduct && !selectedParentProduct
    });
    const isFeaturedColumn = ((_a = product.banner) == null ? void 0 : _a.type) === "recommended-featured";
    const columnStylesDefault = [
      panelValues,
      isSelected && !isCurrentProduct ? panelValuesSelected : ""
    ];
    const columnStylesWhenFeatured = [
      panelValuesFeatured,
      isSelected && !isCurrentProduct ? panelValuesFeaturedSelected : ""
    ];
    return /* @__PURE__ */ jsx(
      ComparativesMatrixColumn,
      {
        index: index + 1,
        numOfColumns,
        gridRowGap,
        isSelected,
        isCurrentProduct,
        customStyles: [
          panelReset,
          ...isFeaturedColumn ? columnStylesWhenFeatured : columnStylesDefault
        ],
        children: attributes.map((attribute, attributeIndex) => {
          var _a2;
          const value = (_a2 = product.comparatives) == null ? void 0 : _a2.content.find(
            ({ key }) => key === attribute.key
          );
          return /* @__PURE__ */ jsx(
            ComparativeCellWrapper,
            {
              isLastCell: getIsLastCell({
                attributes,
                attributeIndex
              }),
              children: /* @__PURE__ */ jsx(ComparativeValueCell, { value })
            },
            attribute.key
          );
        })
      },
      product.id
    );
  })
] });
const ComparativesMatrixSectionTitle = ({ title }) => /* @__PURE__ */ jsx(Box, { style: { borderBottom: "1px solid #5A6881" }, paddingBottom: "gutter", children: /* @__PURE__ */ jsx(Heading, { level: "4", align: "left", children: title }) });
const ComparativesMatrixColumn = ({
  numOfColumns,
  index = 0,
  gridRowGap,
  children,
  isSelected = false,
  isCurrentProduct = false,
  customStyles
}) => /* @__PURE__ */ jsx(
  GridColumn,
  {
    className: [
      columnPanel,
      getPanelStyle({
        panel: { variant: "default" },
        isSelected,
        isCurrentProduct,
        productType: "AdProduct"
      }),
      isSelected && !isCurrentProduct ? panel : "",
      ...customStyles ?? []
    ],
    gridColumns: getGridColumn(index, numOfColumns),
    gridTemplateAreas: GRID_TEMPLATE_AREAS,
    gridRowGap,
    children
  }
);
const ComparativeCellWrapper = ({
  key,
  isLastCell,
  children
}) => /* @__PURE__ */ jsx(
  Box,
  {
    style: {
      gridArea: key,
      borderBottom: !isLastCell ? "1px solid #eee" : "none"
    },
    paddingTop: "xsmall",
    paddingBottom: "medium",
    marginBottom: !isLastCell ? "xsmall" : "none",
    children
  }
);
const ComparativeHeadingCell = ({
  attribute,
  variant
}) => /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
  /* @__PURE__ */ jsxs(Columns, { space: "small", alignY: variant === "compact" ? "center" : "top", children: [
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Text, { weight: "strong", children: attribute.title }) }),
    /* @__PURE__ */ jsx(Column, { width: "content", children: variant === "compact" && attribute.description || attribute.helpTip ? /* @__PURE__ */ jsx(
      HelpTip,
      {
        name: attribute.title,
        helpTip: attribute.helpTip ?? { content: attribute.description },
        tone: "secondary",
        tracking: { id: attribute.key }
      }
    ) : null })
  ] }),
  variant === "default" && attribute.description ? /* @__PURE__ */ jsx(Text, { children: attribute.description }) : null
] });
const ComparativeValueCell = ({
  value
}) => /* @__PURE__ */ jsx(
  Box,
  {
    display: "flex",
    alignItems: "center",
    height: "full",
    width: "full",
    justifyContent: "center",
    children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      (value == null ? void 0 : value.type) === "included" && !(value == null ? void 0 : value.content) ? /* @__PURE__ */ jsx(
        Box,
        {
          background: "positiveLight",
          borderRadius: "full",
          style: { padding: "3px" },
          children: /* @__PURE__ */ jsx(IconTick, { size: "small" })
        }
      ) : null,
      (value == null ? void 0 : value.type) === "excluded" && !(value == null ? void 0 : value.content) || !value ? /* @__PURE__ */ jsx(IconClear, {}) : null,
      (value == null ? void 0 : value.content) ? /* @__PURE__ */ jsx(Text, { children: value == null ? void 0 : value.content }) : null
    ] })
  }
);
export {
  ComparativesMatrix
};
